import { useRef, useState } from "react";
import { Bold, Italic, Heading1, Heading2, Heading3, List, Link as LinkIcon, Eye, Pencil } from "lucide-react";

const renderMd = (md) => {
  if (!md) return null;
  const lines = md.split("\n");
  const out = [];
  let buf = [];
  const flush = () => {
    if (buf.length) {
      out.push(<ul key={`ul-${out.length}`} className="list-disc pl-6 space-y-1.5 my-3">{buf}</ul>);
      buf = [];
    }
  };
  const inline = (text) => {
    // Bold **x**
    const parts = [];
    let i = 0;
    const re = /(\*\*([^*]+)\*\*|\*([^*]+)\*|\[([^\]]+)\]\(([^)]+)\))/g;
    let m, last = 0;
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) parts.push(text.slice(last, m.index));
      if (m[2]) parts.push(<strong key={i++}>{m[2]}</strong>);
      else if (m[3]) parts.push(<em key={i++}>{m[3]}</em>);
      else if (m[4]) parts.push(<a key={i++} href={m[5]} target="_blank" rel="noopener noreferrer" className="text-accent underline underline-offset-2">{m[4]}</a>);
      last = m.index + m[0].length;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts;
  };
  lines.forEach((ln, idx) => {
    if (ln.startsWith("# ")) { flush(); out.push(<h1 key={idx} className="font-heading text-3xl sm:text-4xl font-bold mt-6 mb-3">{inline(ln.slice(2))}</h1>); }
    else if (ln.startsWith("## ")) { flush(); out.push(<h2 key={idx} className="font-heading text-2xl font-semibold mt-6 mb-2">{inline(ln.slice(3))}</h2>); }
    else if (ln.startsWith("### ")) { flush(); out.push(<h3 key={idx} className="font-heading text-lg font-semibold mt-4 mb-2">{inline(ln.slice(4))}</h3>); }
    else if (ln.startsWith("- ")) { buf.push(<li key={idx}>{inline(ln.slice(2))}</li>); }
    else if (ln.trim()) { flush(); out.push(<p key={idx} className="my-2.5 leading-relaxed">{inline(ln)}</p>); }
    else { flush(); }
  });
  flush();
  return out;
};

const Btn = ({ onClick, title, children, active }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`inline-flex items-center gap-1 px-2.5 h-8 rounded-lg text-xs font-medium transition-colors ${active ? "bg-foreground text-background" : "hover:bg-muted text-muted-foreground hover:text-foreground"}`}
  >
    {children}
  </button>
);

export default function RichTextEditor({ value, onChange, label, hint, rows = 14 }) {
  const ref = useRef(null);
  const [preview, setPreview] = useState(false);

  const apply = (transform) => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const before = value.slice(0, start);
    const sel = value.slice(start, end);
    const after = value.slice(end);
    const { text, cursor } = transform(sel);
    onChange(before + text + after);
    setTimeout(() => {
      el.focus();
      const pos = before.length + (cursor ?? text.length);
      el.setSelectionRange(pos, pos);
    }, 0);
  };

  const wrapLine = (prefix) => apply((sel) => {
    const lines = (sel || "Заголовок").split("\n").map((l) => prefix + l.replace(/^#+\s*/, "").replace(/^-\s*/, ""));
    return { text: lines.join("\n") };
  });

  const wrapInline = (wrap, placeholder = "текст") => apply((sel) => {
    const txt = sel || placeholder;
    return { text: `${wrap}${txt}${wrap}` };
  });

  const addLink = () => {
    const url = window.prompt("URL ссылки:", "https://");
    if (!url) return;
    apply((sel) => ({ text: `[${sel || "текст ссылки"}](${url})` }));
  };

  return (
    <div className="space-y-2">
      {label && <div className="text-sm font-semibold">{label}</div>}
      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="flex flex-wrap items-center gap-1 p-2 border-b border-border bg-muted/30">
          <Btn onClick={() => wrapLine("# ")} title="Заголовок 1"><Heading1 className="size-3.5" /> H1</Btn>
          <Btn onClick={() => wrapLine("## ")} title="Заголовок 2"><Heading2 className="size-3.5" /> H2</Btn>
          <Btn onClick={() => wrapLine("### ")} title="Заголовок 3"><Heading3 className="size-3.5" /> H3</Btn>
          <div className="w-px h-5 bg-border mx-1" />
          <Btn onClick={() => wrapInline("**", "жирный текст")} title="Жирный"><Bold className="size-3.5" /></Btn>
          <Btn onClick={() => wrapInline("*", "курсив")} title="Курсив"><Italic className="size-3.5" /></Btn>
          <Btn onClick={() => wrapLine("- ")} title="Маркер"><List className="size-3.5" /></Btn>
          <Btn onClick={addLink} title="Ссылка"><LinkIcon className="size-3.5" /></Btn>
          <div className="flex-1" />
          <Btn onClick={() => setPreview(false)} active={!preview} title="Редактирование"><Pencil className="size-3.5" /> Текст</Btn>
          <Btn onClick={() => setPreview(true)} active={preview} title="Предпросмотр"><Eye className="size-3.5" /> Превью</Btn>
        </div>
        {preview ? (
          <div className="p-5 min-h-[200px] prose-custom" data-testid="rich-preview">
            {renderMd(value) || <span className="text-muted-foreground italic">Пусто</span>}
          </div>
        ) : (
          <textarea
            ref={ref}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            rows={rows}
            spellCheck
            className="w-full p-5 bg-background text-sm leading-relaxed focus:outline-none resize-y"
            placeholder="Начните писать..."
          />
        )}
      </div>
      {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}
